import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FormModal } from "@/components/pawkt/FormModal";
import { Pencil, Wrench, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

const KEYS = ["board-occ", "board-res", "board-dash", "board-stays"];
const useRefresh = () => { const qc = useQueryClient(); return () => KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
const TYPES = [{ value: "kennel", label: "Kennel" }, { value: "room", label: "Room" }, { value: "suite", label: "Suite" }, { value: "cage", label: "Cage" }];

export function EditUnitButton({ unit }) {
  const [open, setOpen] = useState(false);
  const refresh = useRefresh();
  const save = async (v) => {
    try { await api.put(`/boarding/resources/${unit.resource_id}`, { name: v.name, type: v.type, capacity: Number(v.capacity) }); toast.success("Unit updated"); refresh(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`unit-edit-${unit.resource_id}`} title="Edit unit" className="rounded-md p-1 text-slate-500 hover:bg-white hover:text-slate-900"><Pencil className="h-3.5 w-3.5" /></button>
      <FormModal open={open} onOpenChange={setOpen} title="Edit unit" testid="unit-edit-modal"
        initial={{ name: unit.name, type: unit.type || "kennel", capacity: unit.capacity }}
        fields={[
          { name: "name", label: "Name / number", full: true, validate: (x) => (String(x || "").trim() ? "" : "Name is required") },
          { name: "type", label: "Type", type: "select", options: TYPES.some((t) => t.value === unit.type) || !unit.type ? TYPES : [...TYPES, { value: unit.type, label: unit.type.replace("_", " ") }] },
          { name: "capacity", label: "Capacity (pets)", type: "number", hint: "Pets of the same customer can share up to this number.",
            validate: (x) => (Number.isInteger(Number(x)) && Number(x) >= 1 && Number(x) <= 50 ? "" : "Whole number between 1 and 50") },
        ]} onSubmit={save} submitLabel="Save" />
    </>
  );
}

export function UnitStatusButton({ unit }) {
  const [open, setOpen] = useState(false);
  const [st, setSt] = useState(unit.unit_status || "available");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useRefresh();
  const { data: aff } = useQuery({ queryKey: ["unit-affected", unit.resource_id], enabled: open && st !== "available",
    queryFn: async () => (await api.get(`/boarding/resources/${unit.resource_id}/affected`)).data.items });
  const blocked = st !== "available" && (aff || []).length > 0;
  const save = async () => {
    setBusy(true);
    try { await api.post(`/boarding/resources/${unit.resource_id}/status`, { status: st, reason }); toast.success("Status updated"); refresh(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => { setSt(unit.unit_status || "available"); setReason(""); setOpen(true); }} data-testid={`unit-status-${unit.resource_id}`} title="Change status" className="rounded-md p-1 text-slate-500 hover:bg-white hover:text-slate-900"><Wrench className="h-3.5 w-3.5" /></button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="unit-status-dialog">
            <DialogHeader><DialogTitle className="font-display">{unit.name} status</DialogTitle></DialogHeader>
            <DialogDescription className="text-xs text-slate-500">Occupied is shown automatically while pets are checked in. Units in maintenance or unavailable can't take new stays.</DialogDescription>
            <select value={st} onChange={(e) => setSt(e.target.value)} data-testid="unit-status-select" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="available">Available (in service)</option>
              <option value="maintenance">Maintenance</option>
              <option value="unavailable">Unavailable</option>
            </select>
            {st !== "available" && <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (required)" data-testid="unit-status-reason" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />}
            {blocked && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="unit-status-affected">
                <p className="font-semibold">Move these stays to another unit first:</p>
                {aff.map((a) => <p key={a.stay_id} className="mt-1">{a.pets.filter(Boolean).join(", ") || "Pet"} · {a.status.replace("_", " ")} · {fdate(a.check_in)} → {fdate(a.expected_check_out)}</p>)}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy || blocked || (st !== "available" && !reason.trim())} data-testid="unit-status-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// Stand-alone stays (no booking). Booking-linked stays use ReallocateButton.
export function StayRelocateButton({ stay }) {
  const [open, setOpen] = useState(false);
  const [rid, setRid] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useRefresh();
  const { data } = useQuery({ queryKey: ["stay-avail", stay.id], enabled: open,
    queryFn: async () => (await api.get("/boarding/availability", { params: { start_time: stay.check_in, end_time: stay.expected_check_out || stay.check_out || stay.check_in,
      customer_id: stay.customer_id || "", pet_ids: stay.pet_id || "", branch_id: stay.branch_id || "" } })).data.items });
  const opts = (data || []).filter((r) => r.id !== stay.resource_id);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/boarding/stays/${stay.id}/relocate`, { resource_id: rid, reason }); toast.success("Stay moved. Charges unchanged."); refresh(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`stay-relocate-${stay.id}`} className="h-7 gap-1 text-xs"><ArrowRightLeft className="h-3 w-3" /> Move</Button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="stay-relocate-dialog">
            <DialogHeader><DialogTitle className="font-display">Move stay</DialogTitle></DialogHeader>
            <DialogDescription className="text-xs text-slate-500">{fdate(stay.check_in)} → {fdate(stay.expected_check_out)}. Units that can't take this pet for the whole stay are disabled with the reason.</DialogDescription>
            <select value={rid} onChange={(e) => setRid(e.target.value)} data-testid="stay-relocate-select" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">Select a destination…</option>
              {opts.map((r) => <option key={r.id} value={r.id} disabled={!r.available}>{r.name} · {r.occupied}/{r.capacity}{r.available ? "" : ` — ${r.reason}`}</option>)}
            </select>
            {data && !opts.some((r) => r.available) && <p className="text-xs text-rose-600" data-testid="stay-relocate-none">No unit is free for these dates. Free a unit or change the dates first.</p>}
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (required)" data-testid="stay-relocate-reason" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!rid || !reason.trim() || busy} data-testid="stay-relocate-save" className="bg-slate-900 text-white">{busy ? "Moving…" : "Confirm move"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
